import { useState } from 'react';
import {
  Space,
  Title,
  Text,
  Card as BariCard,
  Tag,
  Tooltip,
} from '@bancobarigui/barihaus';
import { Progress } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  DollarOutlined,
  AreaChartOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  RightOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { AiAnalysisResult, AvmResult, DeduplicationResult, VisualInspectionResult } from './types';
import styles from './RelatorioAnaliseIA.module.css';

interface RelatorioAnaliseIAProps {
  analysisResult: AiAnalysisResult;
  loading?: boolean;
  onPropertyClick?: (propertyId: string) => void;
  onImportSamples?: () => void;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatNumber = (value: number, decimals = 2): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

// Componente de resumo da avaliação AVM - Design Minimalista
function AvmSection({ data, onImportSamples }: { data: AvmResult; onImportSamples?: () => void }) {
  const resultado = data.resultado_target;

  if (!resultado) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <DollarOutlined className={styles.cardIcon} />
            <div>
              <h3 className={styles.cardTitle}>Estimativa de Valor do Imóvel</h3>
              <span className={styles.cardSubtitle}>Previsão baseada em imóveis similares da região</span>
            </div>
          </div>
          <span className={styles.statusError}>Erro</span>
        </div>
        <div className={styles.cardErrorBody}>
          <CloseCircleOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />
          <Text strong style={{ marginTop: 12 }}>Não foi possível calcular o valor</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>{data.error || 'Erro desconhecido'}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <DollarOutlined className={styles.cardIcon} />
          <div>
            <h3 className={styles.cardTitle}>Estimativa de Valor do Imóvel</h3>
            <span className={styles.cardSubtitle}>Previsão baseada em {resultado.amostras_utilizadas} imóveis similares da região</span>
          </div>
        </div>
        <span className={styles.statusSuccess}>Concluída</span>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        {/* Explicação */}
        <div className={styles.sectionExplanation}>
          Este valor foi calculado automaticamente comparando o imóvel inspecionado com {resultado.amostras_utilizadas} anúncios
          similares encontrados na região. A IA analisa características como área, localização e tipologia para estimar o valor de mercado.
        </div>

        {/* Valor principal */}
        <div className={styles.avmValorSection}>
          <span className={styles.avmValorLabel}>Valor Estimado do Imóvel Inspecionado</span>
          <span className={styles.avmValorNumero}>{formatCurrency(resultado.valor_estimado)}</span>
          <span className={styles.avmValorFaixa}>
            {formatCurrency(resultado.valor_minimo)} — {formatCurrency(resultado.valor_maximo)}
          </span>
        </div>

        {/* Métricas */}
        <div className={styles.metricsGrid}>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Valor/m²</span>
            <span className={styles.metricValue}>{formatCurrency(resultado.valor_m2_medio)}</span>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Área</span>
            <span className={styles.metricValue}>{formatNumber(resultado.area_privativa)} m²</span>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Amostras</span>
            <span className={styles.metricValue}>{resultado.amostras_utilizadas}</span>
          </div>
          <div className={styles.metricItem}>
            <span className={styles.metricLabel}>Tipologia</span>
            <span className={styles.metricValue} style={{ textTransform: 'capitalize' }}>{resultado.tipologia}</span>
          </div>
        </div>

        {/* Metodologia */}
        <div className={styles.infoSection}>
          <span className={styles.infoLabel}>Metodologia</span>
          <p className={styles.infoText}>{resultado.metodologia}</p>
        </div>

        {/* Observações */}
        {resultado.observacoes && (
          <div className={styles.infoSection}>
            <span className={styles.infoLabel}>Observações</span>
            <p className={styles.infoText}>{resultado.observacoes}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <span>Emissão: {new Date(resultado.data_emissao).toLocaleDateString('pt-BR')}</span>
        <span>Código: {resultado.codigo_parecer}</span>
      </div>
    </div>
  );
}

// Componente de deduplicação - Design Minimalista
function DeduplicationSection({ data }: { data: DeduplicationResult }) {
  if (data.status === 'skipped' || data.status === 'error') {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <CopyOutlined className={styles.cardIcon} />
            <div>
              <h3 className={styles.cardTitle}>Limpeza de Anúncios Repetidos</h3>
              <span className={styles.cardSubtitle}>Remove anúncios duplicados para evitar distorções na análise</span>
            </div>
          </div>
          <span className={data.status === 'error' ? styles.statusError : styles.statusWarning}>
            {data.status === 'error' ? 'Erro' : 'Não executado'}
          </span>
        </div>
        <div className={styles.cardErrorBody}>
          {data.status === 'error' ? (
            <CloseCircleOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />
          ) : (
            <ExclamationCircleOutlined style={{ fontSize: 28, color: '#faad14' }} />
          )}
          <Text strong style={{ marginTop: 12 }}>Análise não realizada</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>{data.reason || data.error || 'Nenhum detalhe disponível'}</Text>
        </div>
      </div>
    );
  }

  const { relatorio } = data;
  const percentualUnicos = data.total_analisados > 0 ? ((data.total_unicos / data.total_analisados) * 100) : 0;

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <CopyOutlined className={styles.cardIcon} />
          <div>
            <h3 className={styles.cardTitle}>Limpeza de Anúncios Repetidos</h3>
            <span className={styles.cardSubtitle}>Identificamos e removemos anúncios duplicados para maior precisão</span>
          </div>
        </div>
        <span className={styles.statusSuccess}>Concluída</span>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        {/* Explicação */}
        <div className={styles.sectionExplanation}>
          Muitos imóveis aparecem em mais de um site ou são anunciados várias vezes.
          A IA compara as fotos e informações para identificar e remover esses anúncios repetidos,
          garantindo que cada imóvel seja contado apenas uma vez na análise.
        </div>

        {/* Estatísticas */}
        <div className={styles.statsRow}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{data.total_scraping}</span>
            <span className={styles.statLabel}>Encontrados</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{data.total_analisados}</span>
            <span className={styles.statLabel}>Analisados</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue} style={{ color: data.total_duplicatas > 0 ? '#ff4d4f' : '#52c41a' }}>
              {data.total_duplicatas}
            </span>
            <span className={styles.statLabel}>Duplicatas</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue} style={{ color: '#52c41a' }}>{data.total_unicos}</span>
            <span className={styles.statLabel}>Únicos</span>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className={styles.progressSection}>
          <div className={styles.progressHeader}>
            <span>Taxa de únicos</span>
            <span style={{ fontWeight: 600 }}>{Math.round(percentualUnicos)}%</span>
          </div>
          <Progress
            percent={Math.round(percentualUnicos)}
            showInfo={false}
            strokeColor="#1890ff"
            trailColor="#e8e8e8"
            strokeWidth={6}
          />
        </div>

        {/* Grupos de duplicatas */}
        {relatorio?.grupos_duplicatas && relatorio.grupos_duplicatas.length > 0 && (
          <div className={styles.duplicatesSection}>
            <div className={styles.duplicatesHeader}>
              <WarningOutlined style={{ color: '#faad14', marginRight: 6 }} />
              {relatorio.grupos_duplicatas.length} grupo(s) identificados
            </div>
            <div className={styles.duplicatesList}>
              {relatorio.grupos_duplicatas.map((grupo, idx) => (
                <div key={idx} className={styles.duplicateGroup}>
                  <div className={styles.duplicateGroupHeader}>
                    <span className={styles.groupTitle}>Grupo {grupo.grupo_id}</span>
                    <span className={styles.groupBadge}>
                      {grupo.tipo === 'duplicata' ? 'Duplicata' : 'Provável'} • {Math.round(grupo.confianca * 100)}%
                    </span>
                  </div>
                  <div className={styles.duplicateItems}>
                    {grupo.imoveis.map((imovel, iIdx) => (
                      <div key={iIdx} className={styles.duplicateItem}>
                        <span className={styles.itemIndex}>#{imovel.indice}</span>
                        <span className={styles.itemAddress}>{imovel.endereco || 'Endereço não informado'}</span>
                        <span className={styles.itemValue}>{formatCurrency(imovel.valor || 0)}</span>
                      </div>
                    ))}
                  </div>
                  {grupo.motivo && (
                    <div className={styles.groupReason}>
                      <InfoCircleOutlined style={{ marginRight: 4 }} />
                      {grupo.motivo}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.cardFooter}>
        <span>Tempo: {((data.tempo_execucao_ms || 0) / 1000).toFixed(1)}s</span>
        <span>Lotes: {data.num_batches || 0}</span>
      </div>
    </div>
  );
}

// Componente de vistoria visual - Design Minimalista
function VisualInspectionSection({ data, onPropertyClick }: { data: VisualInspectionResult[], onPropertyClick?: (propertyId: string) => void }) {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <EyeOutlined className={styles.cardIcon} />
            <div>
              <h3 className={styles.cardTitle}>Análise Visual dos Comparativos</h3>
              <span className={styles.cardSubtitle}>IA analisa as fotos para identificar padrão e estado de conservação</span>
            </div>
          </div>
          <span className={styles.statusWarning}>Não executado</span>
        </div>
        <div className={styles.cardErrorBody}>
          <ExclamationCircleOutlined style={{ fontSize: 28, color: '#faad14' }} />
          <Text strong style={{ marginTop: 12 }}>Nenhum imóvel analisado</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>A análise visual não foi realizada</Text>
        </div>
      </div>
    );
  }

  const successCount = data.filter(v => v.status === 'success').length;

  const getEstadoLabel = (estado: string) => {
    const e = estado?.toLowerCase();
    if (e === 'otimo' || e === 'ótimo') return 'Ótimo';
    if (e === 'bom') return 'Bom';
    if (e === 'regular') return 'Regular';
    if (e === 'ruim' || e === 'mau') return 'Ruim';
    return estado || '-';
  };

  const handleItemClick = (vistoria: VisualInspectionResult) => {
    if (vistoria.status === 'success' && vistoria.id_imovel && onPropertyClick) {
      onPropertyClick(vistoria.id_imovel);
    }
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderLeft}>
          <EyeOutlined className={styles.cardIcon} />
          <div>
            <h3 className={styles.cardTitle}>Análise Visual dos Comparativos</h3>
            <span className={styles.cardSubtitle}>IA analisou as fotos dos {successCount} imóveis mais similares</span>
          </div>
        </div>
        <span className={styles.statusSuccess}>{successCount}/{data.length} analisados</span>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        {/* Explicação */}
        <div className={styles.sectionExplanation}>
          A IA analisa as fotos de cada imóvel comparativo para identificar o padrão de acabamento,
          estado de conservação e possíveis problemas. Isso ajuda a entender se os imóveis comparativos
          são realmente similares ao imóvel inspecionado.
        </div>

        {/* Hint */}
        <div className={styles.hint}>
          <EyeOutlined style={{ marginRight: 6 }} />
          Clique em um imóvel para visualizá-lo no mapa
        </div>

        {/* Lista de vistorias */}
        <div className={styles.inspectionList}>
          {data.map((vistoria, idx) => (
            <div
              key={idx}
              className={`${styles.inspectionItem} ${vistoria.status === 'success' ? styles.inspectionItemClickable : styles.inspectionItemError}`}
              onClick={() => handleItemClick(vistoria)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleItemClick(vistoria)}
            >
              {vistoria.status === 'success' ? (
                <>
                  {/* Header do item */}
                  <div className={styles.inspectionItemHeader}>
                    <div className={styles.inspectionRanking}>
                      <span className={styles.rankingNum}>#{vistoria.ranking}</span>
                      <span className={styles.rankingType}>{vistoria.tipo_imovel}</span>
                    </div>
                    <RightOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
                  </div>

                  {/* Endereço */}
                  <div className={styles.inspectionAddress}>
                    <HomeOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
                    {vistoria.endereco || 'Endereço não informado'}
                  </div>

                  {/* Detalhes em grid */}
                  <div className={styles.inspectionDetails}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Conservação</span>
                      <span className={styles.detailValue}>{getEstadoLabel(vistoria.estado_conservacao)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Padrão</span>
                      <span className={styles.detailValue}>{vistoria.padrao_acabamento?.replace(/_/g, ' ') || '-'}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Idade</span>
                      <span className={styles.detailValue}>{vistoria.idade_aparente || '-'}</span>
                    </div>
                    {vistoria.estimativa_valor && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>R$/m²</span>
                        <span className={styles.detailValue}>
                          {formatCurrency(vistoria.estimativa_valor.valor_m2_minimo)} - {formatCurrency(vistoria.estimativa_valor.valor_m2_maximo)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Acabamentos */}
                  <div className={styles.finishesRow}>
                    {vistoria.tipo_piso_predominante && (
                      <span className={styles.finishTag}>Piso: {vistoria.tipo_piso_predominante}</span>
                    )}
                    {vistoria.tipo_forro && (
                      <span className={styles.finishTag}>Forro: {vistoria.tipo_forro}</span>
                    )}
                    {vistoria.tipo_esquadrias && (
                      <span className={styles.finishTag}>Esquadrias: {vistoria.tipo_esquadrias}</span>
                    )}
                  </div>

                  {/* Patologias */}
                  {vistoria.patologias && vistoria.patologias.length > 0 && (
                    <div className={styles.pathologiesRow}>
                      <div className={styles.pathologiesIcon}>
                        <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />
                      </div>
                      <div className={styles.pathologiesList}>
                        {vistoria.patologias.map((p, pIdx) => (
                          <span key={pIdx} className={styles.pathologyTag}>
                            {p.tipo} • {p.gravidade}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Impressão geral */}
                  {vistoria.impressao_geral && (
                    <div className={styles.impressionText}>
                      {vistoria.impressao_geral}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.inspectionItemErrorContent}>
                  <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                  <span>Erro na análise #{vistoria.ranking}</span>
                  <span className={styles.errorDetail}>{vistoria.error}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RelatorioAnaliseIA({ analysisResult, loading, onPropertyClick, onImportSamples }: RelatorioAnaliseIAProps) {
  if (loading) {
    return (
      <div className={styles.loadingState}>
        <AreaChartOutlined spin style={{ fontSize: 40, color: '#262626' }} />
        <Title level={4} style={{ marginTop: 16 }}>Processando análise...</Title>
        <Text type="secondary">Isso pode levar alguns minutos</Text>
      </div>
    );
  }

  if (!analysisResult || analysisResult.status === 'error') {
    return (
      <div className={styles.errorState}>
        <CloseCircleOutlined style={{ fontSize: 40, color: '#ff4d4f' }} />
        <Title level={4} style={{ marginTop: 16 }}>Erro na análise</Title>
        <Text type="secondary">{analysisResult?.error || 'Não foi possível processar'}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Relatório de Análise IA</h2>
        <span className={styles.headerSubtitle}>
          {analysisResult.scraping?.total_encontrados || 0} imóveis analisados
        </span>
      </div>

      <div className={styles.sections}>
        {analysisResult.analise && (
          <AvmSection data={analysisResult.analise} onImportSamples={onImportSamples} />
        )}

        {analysisResult.deduplicacao && (
          <DeduplicationSection data={analysisResult.deduplicacao} />
        )}

        {analysisResult.vistoria_visual && (
          <VisualInspectionSection data={analysisResult.vistoria_visual} onPropertyClick={onPropertyClick} />
        )}
      </div>
    </div>
  );
}

export default RelatorioAnaliseIA;
